import { useId, useState } from "react";
import {
  Checkbox,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
} from "@mui/material";
import { type FormikProps } from "formik";
import { useTranslations } from "use-intl";
import { getAgeFromDateOfBirth } from "@/app/lib/utils/getAgefromDateOfBirth";
import { type HotelBooking } from "@/app/lib/types/types";
import MoreDetailsDrawer from "../flight-purchase/MoreDetailsDrawer/MoreDetailsDrawer";
import { formatCurrency } from "@/app/lib/utils/currency";
import type {
  FindHotelsOutputData,
  FindUserTravelersTravelers,
} from "@/app/lib/types/codegen";
import InstallmentDetails from "@/app/ui/flight-purchase/InstallmentDetails";
import { usePaymentContext } from "@/app/lib/context/PaymentContext";
import { includeBnplFee } from "@/app/lib/utils/bnpl-fee";
import { getTravellerAgeAtTravelDate } from "@/app/lib/utils/ageValidation";
import { getTravelerTypeOfAge } from "@/app/lib/utils/transformTraveler";
import { HotelAndRoomCard } from "./HotelAndRoomCard";
import { CheckoutBanner } from "../CheckoutBanner";
import { Feature, isFeatureEnabled } from "@/app/lib/utils/featureFlags";

export interface SummaryFormValues {
  roomAssigments: Record<string, { travelers: string[] }>;
  leadTraveler: string;
  termsAndConditions: boolean;
}

type Props = {
  hotel: FindHotelsOutputData;
  hotelBookingData: HotelBooking;
  form: FormikProps<SummaryFormValues>;
  selectedTravellers: FindUserTravelersTravelers[];
  rooms?: { adults: number; children: number }[];
  travelDate: Date;
};

export const HotelBookingSummary = ({
  hotel,
  hotelBookingData,
  form,
  selectedTravellers,
  rooms = [],
  travelDate,
}: Props) => {
  const t = useTranslations();

  const [moreDetailsOpen, setMoreDetailsOpen] = useState(false);
  const checkboxId = useId();
  const { paymentType } = usePaymentContext();

  const hotelTotalPrice = hotelBookingData.rooms.reduce(
    (acc, room) => acc + (room.price?.value || 0),
    0,
  );

  // Calculate BNPL fee if BNPL is selected
  const includedBnplFee = includeBnplFee(hotelTotalPrice);
  const totalPrice = paymentType === "bnpl" ? includedBnplFee : hotelTotalPrice;

  return (
    <div className="flex w-full flex-col">
      <HotelAndRoomCard
        hotel={hotel}
        hotelBookingData={hotelBookingData}
        rooms={rooms}
      />
      <div className="section-title mt-4">{t("travellers.mainTraveller")}</div>
      <div className="section-card p-0">
        <div className="p-4 pb-2 text-sm text-text-tertiary">
          {t("travellers.markMainTraveller")}
        </div>

        <RadioGroup
          aria-labelledby="cabin-class-radio-group"
          name="leadTraveler"
          value={form.values.leadTraveler}
          onChange={form.handleChange}
        >
          {selectedTravellers.map((traveller) => {
            const ageAtTravelDate = getTravellerAgeAtTravelDate(
              traveller.birthDate,
              travelDate,
            );
            const ageGroupAtTravelDate = getTravelerTypeOfAge(ageAtTravelDate);

            const currentAge = getAgeFromDateOfBirth(traveller.birthDate);
            const currentAgeGroup = getTravelerTypeOfAge(currentAge);

            return (
              <FormControlLabel
                key={traveller.id}
                className="flex gap-4"
                value={traveller.id}
                control={<Radio />}
                disabled={ageGroupAtTravelDate !== "adult"}
                label={
                  <div className="flex items-center gap-2">
                    <div className="grow" data-sentry-mask>
                      <div className="font-medium text-text-title">
                        {traveller.firstName} {traveller.lastName}
                      </div>
                      <div className="text-xs text-text-tertiary">
                        {t("hotels.overview.age")} {currentAge}
                      </div>
                      {ageGroupAtTravelDate !== currentAgeGroup ? (
                        <div className="mt-1 text-xs text-semantic-warning-strong">
                          {t("travellers.ageValidationError", {
                            ageGroupAtTravelDate,
                            age: ageAtTravelDate,
                          })}
                        </div>
                      ) : null}
                    </div>
                    {form.values.leadTraveler === traveller.id ? (
                      <div className="shrink-0 text-xs font-medium">
                        {t("travellers.mainTraveller")}
                      </div>
                    ) : null}
                  </div>
                }
                sx={{
                  m: 0,
                  p: 2,
                  "&:not(:last-of-type)": {
                    borderBottom: "1px solid #E5E5E5",
                  },
                  ".MuiFormControlLabel-label": {
                    width: "100%",
                  },
                }}
              />
            );
          })}
        </RadioGroup>
      </div>

      <div className="section-card mt-2">
        <div className="text-sm text-text-title">
          {t("hotels.bookingPolicy")}
        </div>
      </div>

      {isFeatureEnabled(Feature.CheckoutBanner) ? (
        <div className="mt-4">
          <CheckoutBanner />
        </div>
      ) : null}

      <div className="section-title mt-4">{t("summary")}</div>
      <div className="section-card flex gap-2">
        <div className="shrink-0">
          <Checkbox
            id={checkboxId}
            checked={form.values.termsAndConditions}
            onChange={(e, checked) => {
              form.setFieldValue("termsAndConditions", checked);
            }}
            sx={{ p: 0 }}
          />
        </div>

        <div className="flex flex-col gap-1 pt-1">
          <label
            className="text-sm font-semibold text-text-primary"
            htmlFor={checkboxId}
          >
            {t("hotels.acceptGeneralTerms")}
          </label>
          <div className="text-sm text-text-tertiary">
            {t("hotels.acceptGeneralTermsWarning")}
          </div>
          <Link
            onClick={() => setMoreDetailsOpen(true)}
            sx={{ fontSize: "14px" }}
          >
            {t("moreDetails")}
          </Link>
        </div>
      </div>

      {paymentType === "bnpl" ? (
        <InstallmentDetails totalPrice={totalPrice} />
      ) : (
        <div className="section-card mt-2 flex flex-col gap-2 text-sm">
          <div className="flex justify-between">
            <div className="text-text-tertiary">{t("total")}</div>
            <div className="font-bold text-text-primary">
              <span dir="ltr">
                {formatCurrency(totalPrice, hotelBookingData?.price?.currency)}
              </span>
            </div>
          </div>
          <div className="flex justify-between">
            <div className="text-text-tertiary">{t("paymentMethod")}</div>
            <div className="font-bold text-text-primary">{t("qiCard")}</div>
          </div>
        </div>
      )}

      <MoreDetailsDrawer
        open={moreDetailsOpen}
        onClose={() => setMoreDetailsOpen(false)}
      />
    </div>
  );
};
